import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Input, AutoComplete, notification } from 'antd';
import {
  SearchOutlined,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { debounce } from 'lodash';

import {
  resetSearchParams,
  setHeaderSearch,
  setListingsStatus,
} from 'store/listingsSearchParams/actions';
import { getSearchTypeAndQuery } from 'shared/listingsTable/utils/listingsCloudSearchHelpers';
import apiListing from 'utils/http/apiListing';
import { RootState } from 'store';
import { ListingTableData } from 'shared/interfaces';
import { navigateTo } from 'utils/helpers';
import { regExp } from 'utils/regExp';

import './HeaderSearch.scss';

const { Option } = Select;

export const HeaderSearch = () => {
  const [filter, setFilter] = useState<string>('puppy_id');
  const [searchValue, setSearchValue] = useState<string>('');
  const [listings, setListings] = useState<ListingTableData[]>([]);
  const [total, setTotal] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [autoCompleteVisible, setAutoCompleteVisible] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const { listingsSearchParams } = useSelector((state: RootState) => state);

  const debouncedCallback = useCallback(
    debounce(async (value: string) => {
      try {
        if (value.trim().length >= 3) {
          setLoading(true);
          const query = getSearchTypeAndQuery('search', [], undefined, {
            filter,
            value,
          });

          const { listings, totalListingsResults } =
            await apiListing.cloudSearch(
              {
                ...listingsSearchParams,
                start: 0,
                size: 100,
                query,
              },
              true
            );

          setListings(listings);
          setTotal(totalListingsResults);
          setAutoCompleteVisible(true);
        }
      } catch (error) {
        notification.error({ message: 'Error searching listings' });
      } finally {
        setLoading(false);
      }
    }, 700),
    [filter]
  );

  useEffect(() => {
    clearAll();
    return () => {
      clearAll();
    };
  }, [filter]);

  useEffect(() => {
    setAutoCompleteVisible(false);
    debouncedCallback(searchValue);
  }, [searchValue, debouncedCallback]);

  const clearAll = () => {
    setSearchValue('');
    setTotal(0);
    setListings([]);
  };

  const getType = () => (filter === 'puppy_id' ? 'number' : 'text');

  const onClearSearch = () => setSearchValue('');

  const onChangeInputHandler = (value: string) => {
    let trimmedValue = value;

    const regex =
      getType() === 'number'
        ? regExp.onlyNumber
        : regExp.alphaAndNotSpecialChars;

    if (getType() === 'number') {
      trimmedValue = value.trim();
    }

    if (regex.test(trimmedValue)) {
      setSearchValue(trimmedValue);
    }
  };

  const renderInputSuffix = () => {
    if (Boolean(searchValue) && !loading) {
      return (
        <CloseCircleFilled className="clearInputIcon" onClick={onClearSearch} />
      );
    }
    if (loading) {
      return <LoadingOutlined className="loadingInputIcon" />;
    }
  };

  const renderTitle = (title: string) => (
    <div>
      <span className="dropdownLabel">{title}</span>
      <span className="dropdownLabel" style={{ float: 'right' }}>
        Total listings:
      </span>
    </div>
  );

  const renderItem = () => ({
    value: searchValue,
    label: (
      <div className="HeaderSearch__AutoComplete--head">
        <span>{searchValue}</span>
        <span>{total}</span>
      </div>
    ),
  });

  const options = listings.length
    ? [
        {
          label: renderTitle('Criteria'),
          options: [renderItem()],
        },
      ]
    : [];

  const onResultOptionSelected = (value: string) => {
    dispatch(resetSearchParams());
    dispatch(setHeaderSearch({ filter, value, listings, total }));
    dispatch(setListingsStatus('search'));
    clearAll();
    navigateTo('/listings/search');
  };

  return (
    <section className="HeaderSearch">
      <Select defaultValue={filter} onChange={setFilter}>
        <Option value="puppy_id">Puppy ID</Option>
        <Option value="breeder_puppy_name">Puppy Name</Option>
        <Option value="breeder">Breeder Name</Option>
      </Select>
      <AutoComplete
        className="HeaderSearch__AutoComplete"
        dropdownClassName="HeaderSearch__AutoComplete--dropdown"
        options={options}
        open={autoCompleteVisible}
        onSelect={onResultOptionSelected}
        notFoundContent="Listings not found."
        onChange={onChangeInputHandler}
        value={searchValue}
      >
        <Input
          prefix={<SearchOutlined className="site-form-item-icon" />}
          placeholder="First select a filter and then type your search"
          type="text"
          suffix={renderInputSuffix()}
        />
      </AutoComplete>
    </section>
  );
};
